import React, { useState, useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from 'components/sales/layout/layout';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import localization from 'assets/localization/localization';
import DynamicContentsFormFields from '../DynamicContentsFormFields';
import { fetchDynamicContentView } from 'services/api/dynamicContentsApi';
import {
  emptyFormState,
  buildSubmitPayload,
  mapContentToForm,
  canEditDynamicContent,
} from '../dynamicContentUtils';

const DynamicContentsForm = ({
  addDynamicContent,
  updateDynamicContent,
  isEdit = false,
  contentId,
}) => {
  const { baseUrl } = useContext(BaseUrl);
  const history = useHistory();
  const [formData, setFormData] = useState(emptyFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState('');
  const originalStartAtDisplay = useRef(null);

  useEffect(() => {
    if (!isEdit || !contentId) return;

    let cancelled = false;

    const loadContent = async () => {
      setIsPageLoading(true);
      setLoadError('');
      try {
        const data = await fetchDynamicContentView(contentId);
        if (cancelled) return;

        if (!data) {
          setLoadError('Content not found.');
          return;
        }

        const isActive = data.status === 1 || data.status === '1';
        if (!isActive) {
          toast.error('Inactive content cannot be edited.');
          history.replace(`${baseUrl}/dynamic-contents/view/${contentId}`);
          return;
        }

        if (!canEditDynamicContent(data)) {
          toast.error('You do not have permission to edit this content.');
          history.replace(`${baseUrl}/dynamic-contents/view/${contentId}`);
          return;
        }

        const mapped = mapContentToForm(data);
        originalStartAtDisplay.current = mapped.start_at_display;
        setFormData(mapped);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || 'Failed to load content');
        }
      } finally {
        if (!cancelled) {
          setIsPageLoading(false);
        }
      }
    };

    loadContent();

    return () => {
      cancelled = true;
    };
  }, [isEdit, contentId, baseUrl, history]);

  const handleSubmit = async () => {
    const { payload, error } = buildSubmitPayload(formData, {
      isEdit,
      contentId,
      originalStartAtDisplay: originalStartAtDisplay.current,
    });

    if (error) {
      toast.error(error);
      return;
    }

    setIsLoading(true);

    const res = isEdit
      ? await updateDynamicContent(payload)
      : await addDynamicContent(payload);

    setIsLoading(false);

    if (res?.status === 0) {
      toast.success(
        res?.message ||
          (isEdit
            ? localization.clientDashboardContentUpdated
            : localization.clientDashboardContentCreated)
      );
      setTimeout(() => {
        window.location.href = isEdit
          ? `${baseUrl}/dynamic-contents/view/${contentId}`
          : `${baseUrl}/dynamic-contents`;
      }, 800);
    } else {
      toast.error(res?.message || 'Something went wrong');
    }
  };

  return (
    <Layout
      title={isEdit ? localization.editDynamicContent : localization.addDynamicContent}
      isLoading={isPageLoading || isLoading}
      isGoback
      linkFlag={isEdit}
      linkUrl={isEdit ? `${baseUrl}/dynamic-contents` : undefined}
    >
      {loadError && (
        <div className="alert alert-danger" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && (
        <DynamicContentsFormFields
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          isEdit={isEdit}
        />
      )}
    </Layout>
  );
};

export default DynamicContentsForm;
